import { messageConstants, chatConstants } from "../actions/constants";

import { mergeMessages, messageKey, normalizeMessage, compareMessages } from "../utils/messageMerge";

const initialState = {
    historyRequestsByChat: {},
    deletedMessageIds: {},
    messages: [],
    loading: false,
    sending: false,
    sendingProgress: 0,
    sendError: null,
    forwarding: false,
    deletingById: {},
    error: null,
    nextCursorByChat: {},
    hasMoreByChat: {},
    loadingOlderByChat: {},
};

const getId = (value) => String(value?._id || value || "");

const normalizeReadBy = (readBy = []) => readBy.map((item) => getId(item)).filter(Boolean);

const sortMessages = (messages = []) => messages.slice().sort(compareMessages);
const addMessage = (messages, message, deleted = {}) => (
    message?._id ? mergeMessages(messages, [normalizeMessage(message)], { deleted }) : messages
);

// Capture local changes in each outstanding GET, not in a wall-clock timestamp.
const recordHistoryChanges = (state, action) => {
    const liveTypes = [
        messageConstants.RECEIVE_NEW_MESSAGE, messageConstants.SEND_MESSAGE_SUCCESS,
        messageConstants.SEND_TEXT_SUCCESS, messageConstants.SEND_FILE_SUCCESS,
        messageConstants.SEND_VOICE_SUCCESS, messageConstants.SEND_MESSAGE_OPTIMISTIC_ADD,
        messageConstants.SEND_MESSAGE_OPTIMISTIC_CONFIRM, messageConstants.FORWARD_MESSAGE_SUCCESS,
        'MESSAGE_UPDATED_RECEIVED', 'MESSAGE_REACTIONS_PATCHED',
    ];
    const read = action.type === messageConstants.MARK_MESSAGES_AS_READ_SUCCESS;
    if (!read && !liveTypes.includes(action.type)) return state;
    const patchedMessage = action.type === 'MESSAGE_REACTIONS_PATCHED' && action.payload?.messageId
        ? state.messages.find((item) => getId(item?._id) === getId(action.payload.messageId))
        : null;
    const messages = action.payload?.messages || (action.payload?.message
        ? [action.payload.message]
        : (patchedMessage ? [patchedMessage] : []));
    const requests = { ...state.historyRequestsByChat };
    for (const [chatId, lanes] of Object.entries(requests)) {
        const changedKeys = messages.filter((message) => getId(message.chatId) === chatId ||
            (message.__starPatch && state.messages.some((item) => getId(item._id) === getId(message._id) && getId(item.chatId) === chatId)))
            .map(messageKey);
        const reader = read && getId(action.payload?.chatId) === chatId ? getId(action.payload?.userId) : "";
        if (!changedKeys.length && !reader) continue;
        requests[chatId] = Object.fromEntries(Object.entries(lanes).map(([lane, request]) => [lane, {
            ...request,
            changed: { ...request.changed, ...Object.fromEntries(changedKeys.map((key) => [key, true])) },
            readUsers: reader ? [...new Set([...request.readUsers, reader])] : request.readUsers,
        }]));
    }
    return { ...state, historyRequestsByChat: requests };
};

const getReplyMessageId = (message) => (
    getId(message?.replyTo?.messageId)
);

/**
 * Отправленное своё фото не мигает: пока не скачалась копия с сервера,
 * показываем то, что уже было на экране (файл с компьютера).
 */
const keepLocalPreview = (message, local) => (
    message && local?.__localUrl
        ? { ...message, __localUrl: local.__localUrl, __clientMessageId: local.__clientMessageId }
        : message
);

const findMatchingOptimisticIndex = (messages, incoming, meId) => {
    if (!incoming) return -1;
    if (messages.some((item) => messageKey(item) === messageKey(incoming))) return -1;

    // Надёжнее всего — по номеру сообщения, который придумал браузер.
    const clientMessageId = String(incoming.clientMessageId || "");
    if (clientMessageId) {
        const byClientId = messages.findIndex((item) => (
            String(item?.__clientMessageId || "") === clientMessageId
        ));
        if (byClientId >= 0) return byClientId;
    }

    // Старый сервер номер не возвращает — ищем текст без файла с тем же содержимым.
    if (!meId || incoming.fileName || incoming.fileType) return -1;
    if (getId(incoming.sender) !== getId(meId)) return -1;

    const incomingChatId = getId(incoming.chatId);
    const incomingText = String(incoming.text || "");
    const incomingReplyId = getReplyMessageId(incoming);

    return messages.findIndex((item) => (
        item?.__sendStatus === "sending" &&
        !item?.fileName &&
        getId(item.chatId) === incomingChatId &&
        String(item.text || "") === incomingText &&
        getReplyMessageId(item) === incomingReplyId
    ));
};

const mergeRealtimeMessageUpdate = (current, incoming) => {
    if (!current || !incoming) return current || incoming;

    const localFields = Object.fromEntries(
        Object.entries(current).filter(([key]) => key.startsWith('__'))
    );

    const readBy = Array.from(new Set([
        ...(current.readBy || []).map(getId),
        ...(incoming.readBy || []).map(getId),
    ].filter(Boolean)));

    return normalizeMessage({
        ...current,
        ...incoming,
        ...localFields,
        readBy,
    });
};

export default function messageReducer(state = initialState, action) {
    state = recordHistoryChanges(state, action);
    switch (action.type) {
        case messageConstants.GET_MESSAGES_REQUEST:
        case messageConstants.GET_MESSAGES_OLDER_REQUEST: {
            const chatId = getId(action.payload?.chatId);
            const append = action.type === messageConstants.GET_MESSAGES_OLDER_REQUEST;
            return {
                ...state,
                loading: append ? state.loading : true,
                error: null,
                historyRequestsByChat: {
                    ...state.historyRequestsByChat,
                    [chatId]: {
                        ...(append ? state.historyRequestsByChat[chatId] : {}),
                        [append ? "older" : "latest"]: { id: action.payload?.requestId, changed: {}, readUsers: [] },
                    },
                },
                loadingOlderByChat: { ...state.loadingOlderByChat, [chatId]: append },
            };
        }

        case messageConstants.GET_MESSAGES_SUCCESS: {
            const chatId = getId(action.payload?.chatId);
            const incoming = (action.payload?.messages || []).map((item) => normalizeMessage(item, chatId));
            const append = !!action.payload?.append;
            const nextCursor = action.payload?.nextCursor || null;
            const hasMore = !!action.payload?.hasMore;

            const lane = append ? "older" : "latest";
            const request = state.historyRequestsByChat[chatId]?.[lane];
            if (!chatId || !request || request.id !== action.payload?.requestId) return state;
            const remainingRequests = { ...state.historyRequestsByChat[chatId] };
            delete remainingRequests[lane];

            const otherMessages = state.messages.filter((message) => getId(message.chatId) !== chatId);
            const currentMessages = state.messages.filter((message) => getId(message.chatId) === chatId);
            const nextChatMessages = mergeMessages(currentMessages, incoming, {
                append, nextCursor, changed: request.changed, readUsers: request.readUsers,
                deleted: state.deletedMessageIds,
            });

            return {
                ...state,
                messages: sortMessages([...otherMessages, ...nextChatMessages]),
                historyRequestsByChat: { ...state.historyRequestsByChat, [chatId]: remainingRequests },
                loading: false,
                error: null,
                nextCursorByChat: {
                    ...state.nextCursorByChat,
                    [chatId]: nextCursor,
                },
                hasMoreByChat: {
                    ...state.hasMoreByChat,
                    [chatId]: hasMore,
                },
                loadingOlderByChat: {
                    ...state.loadingOlderByChat,
                    [chatId]: false,
                },
            };
        }

        case messageConstants.GET_MESSAGES_FAILURE:
        case messageConstants.GET_MESSAGES_OLDER_FAILURE: {
            const chatId = getId(action.payload?.chatId);
            const append = action.type === messageConstants.GET_MESSAGES_OLDER_FAILURE;
            const lane = append ? "older" : "latest";
            if (state.historyRequestsByChat[chatId]?.[lane]?.id !== action.payload?.requestId) return state;
            const remaining = { ...state.historyRequestsByChat[chatId] };
            delete remaining[lane];
            return {
                ...state, loading: append ? state.loading : false,
                error: action.payload?.error || "Ошибка загрузки сообщений",
                historyRequestsByChat: { ...state.historyRequestsByChat, [chatId]: remaining },
                loadingOlderByChat: { ...state.loadingOlderByChat, [chatId]: false },
            };
        }

        case messageConstants.SEND_MESSAGE_OPTIMISTIC_ADD:
            return {
                ...state,
                messages: addMessage(state.messages, action.payload?.message, state.deletedMessageIds),
                sendError: null,
            };

        case messageConstants.SEND_MESSAGE_OPTIMISTIC_CONFIRM: {
            const clientMessageId = String(action.payload?.clientMessageId || "");
            const confirmedMessage = action.payload?.message || null;
            const local = state.messages.find((message) => (
                String(message?.__clientMessageId || "") === clientMessageId
            ));
            const withoutLocal = state.messages.filter((message) => (
                String(message?.__clientMessageId || "") !== clientMessageId
            ));

            return {
                ...state,
                messages: confirmedMessage
                    ? addMessage(withoutLocal, keepLocalPreview(confirmedMessage, local), state.deletedMessageIds)
                    : withoutLocal,
                sendError: null,
            };
        }

        case "SEND_MESSAGE_OPTIMISTIC_PROGRESS": {
            const clientMessageId = String(action.payload?.clientMessageId || "");
            const progress = Math.min(100, Math.max(0, Number(action.payload?.progress || 0)));

            return {
                ...state,
                messages: state.messages.map((message) => (
                    String(message?.__clientMessageId || "") === clientMessageId &&
                    message.__sendStatus === "sending"
                        ? { ...message, __uploadProgress: progress }
                        : message
                )),
            };
        }

        case "SEND_MESSAGE_OPTIMISTIC_RETRY": {
            const clientMessageId = String(action.payload?.clientMessageId || "");

            return {
                ...state,
                messages: state.messages.map((message) => (
                    String(message?.__clientMessageId || "") === clientMessageId
                        ? { ...message, __optimistic: true, __sendStatus: "sending", __sendError: "", __uploadProgress: 0 }
                        : message
                )),
                sendError: null,
            };
        }

        case "SEND_MESSAGE_OPTIMISTIC_DISCARD": {
            const clientMessageId = String(action.payload?.clientMessageId || "");
            const local = state.messages.find((message) => (
                String(message?.__clientMessageId || "") === clientMessageId
            ));
            if (local?.__localUrl && typeof URL !== "undefined") {
                try { URL.revokeObjectURL(local.__localUrl); } catch (_error) { /* уже освобождён */ }
            }

            return {
                ...state,
                messages: state.messages.filter((message) => (
                    String(message?.__clientMessageId || "") !== clientMessageId
                )),
                sendError: null,
            };
        }

        case messageConstants.SEND_MESSAGE_OPTIMISTIC_FAILURE: {
            const clientMessageId = String(action.payload?.clientMessageId || "");
            const error = action.payload?.error || "Не удалось отправить сообщение";

            return {
                ...state,
                messages: state.messages.map((message) => (
                    String(message?.__clientMessageId || "") === clientMessageId
                        ? {
                            ...message,
                            __optimistic: false,
                            __sendStatus: "failed",
                            __sendError: error,
                        }
                        : message
                )),
                sendError: error,
            };
        }

        case messageConstants.SEND_MESSAGE_REQUEST:
            return {
                ...state,
                sending: true,
                sendingProgress: 0,
                sendError: null,
            };

        case messageConstants.SEND_MESSAGE_PROGRESS:
            return {
                ...state,
                sendingProgress: Math.min(100, Math.max(0, Number(action.payload?.progress || 0))),
            };

        case messageConstants.SEND_MESSAGE_SUCCESS:
        case messageConstants.SEND_TEXT_SUCCESS:
        case messageConstants.SEND_FILE_SUCCESS:
        case messageConstants.SEND_VOICE_SUCCESS:
            return {
                ...state,
                messages: addMessage(state.messages, action.payload?.message, state.deletedMessageIds),
                sending: false,
                sendingProgress: 100,
                sendError: null,
            };

        case messageConstants.SEND_MESSAGE_FAILURE:
            return {
                ...state,
                sending: false,
                sendingProgress: 0,
                sendError: action.payload?.error || "Не удалось отправить сообщение",
            };

        case messageConstants.FORWARD_MESSAGE_REQUEST:
            return {
                ...state,
                forwarding: true,
                sendError: null,
            };

        case messageConstants.FORWARD_MESSAGE_SUCCESS: {
            const forwardedMessages = Array.isArray(action.payload?.messages)
                ? action.payload.messages
                : (action.payload?.message ? [action.payload.message] : []);

            return {
                ...state,
                messages: mergeMessages(state.messages, forwardedMessages, { deleted: state.deletedMessageIds }),
                forwarding: false,
                sendError: null,
            };
        }

        case messageConstants.FORWARD_MESSAGE_FAILURE:
            return {
                ...state,
                forwarding: false,
                sendError: action.payload?.error || "Не удалось переслать сообщение",
            };

        case messageConstants.RECEIVE_NEW_MESSAGE: {
            const message = normalizeMessage(action.payload?.message);

            if (message?.__starPatch) {
                return {
                    ...state,
                    messages: state.messages.map((item) => (
                        String(item._id) === String(message._id)
                            ? { ...item, starredBy: message.starredBy || item.starredBy || [] }
                            : item
                    )),
                };
            }

            const optimisticIndex = findMatchingOptimisticIndex(
                state.messages,
                message,
                action.payload?.meId
            );

            if (optimisticIndex >= 0) {
                const nextMessages = state.messages.slice();
                const [local] = nextMessages.splice(optimisticIndex, 1);

                return {
                    ...state,
                    messages: addMessage(nextMessages, keepLocalPreview(message, local), state.deletedMessageIds),
                };
            }

            return {
                ...state,
                messages: addMessage(state.messages, message, state.deletedMessageIds),
            };
        }

        case 'MESSAGE_UPDATED_RECEIVED': {
            const incoming = normalizeMessage(action.payload?.message);
            const messageId = getId(incoming?._id);
            if (!messageId) return state;

            let found = false;
            const messages = state.messages.map((current) => {
                if (getId(current?._id) !== messageId) return current;
                found = true;
                return mergeRealtimeMessageUpdate(current, incoming);
            });

            return found ? { ...state, messages } : state;
        }

        case 'MESSAGE_REACTIONS_PATCHED': {
            const messageId = getId(action.payload?.messageId);
            if (!messageId) return state;

            return {
                ...state,
                messages: state.messages.map((message) => (
                    getId(message?._id) === messageId
                        ? { ...message, reactions: action.payload?.reactions || [] }
                        : message
                )),
            };
        }

        case messageConstants.DELETE_MESSAGE_REQUEST: {
            const messageId = getId(action.payload?.messageId);
            return {
                ...state,
                deletingById: {
                    ...state.deletingById,
                    [messageId]: true,
                },
            };
        }

        case messageConstants.DELETE_MESSAGE_SUCCESS: {
            const messageId = getId(action.payload?.messageId);
            const nextDeleting = { ...state.deletingById };
            delete nextDeleting[messageId];

            return {
                ...state,
                messages: state.messages.filter((message) => String(message._id) !== messageId),
                deletedMessageIds: { ...state.deletedMessageIds, [messageId]: true },
                deletingById: nextDeleting,
            };
        }

        case messageConstants.DELETE_MESSAGE_FAILURE: {
            const messageId = getId(action.payload?.messageId);
            const nextDeleting = { ...state.deletingById };
            delete nextDeleting[messageId];

            return {
                ...state,
                deletingById: nextDeleting,
                error: action.payload?.error || "Ошибка удаления сообщения",
            };
        }

        case "LOGOUT_REQUEST":
        case "LOGOUT_SUCCESS":
        case "LOGOUT_BY_SESSION_EXPIRED":
        case messageConstants.CLEAR_MESSAGES:
            return { ...initialState };

        case chatConstants.REMOVE_CHAT: {
            const chatId = getId(action.payload?.chatId);
            const requests = { ...state.historyRequestsByChat };
            delete requests[chatId];
            return { ...state, historyRequestsByChat: requests,
                messages: state.messages.filter((message) => getId(message.chatId) !== chatId) };
        }

        case messageConstants.MARK_MESSAGES_AS_READ_SUCCESS: {
            const chatId = getId(action.payload?.chatId);
            const userId = getId(action.payload?.userId);

            if (!chatId || !userId) return state;

            return {
                ...state,
                messages: state.messages.map((message) => {
                    const messageChatId = getId(message.chatId);
                    const senderId = getId(message.sender);

                    if (message.type === "call" || messageChatId !== chatId || senderId === userId) return message;

                    const readByIds = normalizeReadBy(message.readBy);
                    if (readByIds.includes(userId)) return message;

                    return {
                        ...message,
                        readBy: [...(message.readBy || []), userId],
                    };
                }),
            };
        }

        default:
            return state;
    }
}
